/* "Add to group" — the points list's bulk action.
 *
 * Replaces the legacy `point/Grouping.js` + `point/AddGroup.js` slide-in panel, which was a
 * second, worse copy of `/point_groups`: it listed every group with edit / clone / delete, and
 * its editor re-implemented the name, the "available for" picker and the point list that the
 * descriptor-driven page already renders. It also took over the table's checkboxes for the
 * duration of a "grouping session", so the same checkbox meant two things depending on hidden
 * state, and it fed the group bare row ids while rendering `point.name` — so every chip was blank.
 *
 * This does ONE thing: take the selected points and put them in a group, existing or new.
 * Managing groups themselves (rename, share, delete, edit membership) is `/point_groups`, linked
 * from the footer. Clone is the one capability that went away with the panel.
 *
 * Membership is merged, never replaced: the group is fetched on pick and PATCHed with the union,
 * so adding 3 points to a 900-point group cannot silently drop the 900.
 */
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Empty, Flex, Form, Input, Modal, Segmented, Tag, Typography, Upload } from "antd";
import { GroupOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";

import EntitySelect from "../../../component/EntitySelect";
import { TAKS_IMPORT } from "../../../config/lang";
import Alert from "../../../util/alert";
import { call, upload } from "../../../util/action";
import { fill, translate } from "../../../util/util";

import "./add-to-group.scss";

/* Relation pickers ask for `active=true` so a deactivated group is not offered (see
   `active_only` in picsell-api). Module-scope so the reference is stable — `EntitySelect`
   keys its option cache on it. */
const ACTIVE_ONLY = { active: true };

/* ponytail: "add all" can stage thousands of points; only the first 200 chips render and the
   rest are counted. Add windowing if a group ever needs per-point removal at that size. */
const SHOWN = 200;

const MIN_NAME = 5;

const byId = list => Array.from(new Map(list.map(p => [p.id, p])).values());

export default function AddToGroup({ points = [], query = "", total = 0, currentUser, onDone }) {
  const history = useHistory();
  const lang = currentUser?.language || "en";
  const t = key => translate(TAKS_IMPORT, key, lang);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing");
  const [pending, setPending] = useState([]);
  const [group, setGroup] = useState(null);
  /* The picked group's current membership — the merge base, and what lets the form say how many
     of the selection are already in it before anything is written. */
  const [members, setMembers] = useState(null);
  const [name, setName] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [busy, setBusy] = useState(false);

  const start = () => {
    setMode("existing");
    setPending(points);
    setGroup(null);
    setMembers(null);
    setName("");
    setSupervisors([]);
    setOpen(true);
  };

  const pickGroup = option => {
    setGroup(option);
    setMembers(null);
    if (!option) return;
    call({ method: "GET", url: `point_groups/${option.id}/` }, response => {
      setMembers(Array.isArray(response?.points) ? response.points.map(p => p.id) : []);
    });
  };

  const addAllFiltered = () => {
    /* The same filters the table is showing, minus the paging — `points/all-points/` runs the
       list's own filterset, so a filtered "all" is exactly what the user is looking at. */
    const p = new URLSearchParams(query);
    ["page", "page_size", "ordering"].forEach(key => p.delete(key));
    setBusy(true);
    call({ method: "GET", url: `points/all-points/${p.toString() ? `?${p}` : ""}` }, response => {
      setBusy(false);
      if (!Array.isArray(response?.points)) return Alert.error(t("new_group_popup_error_update_text"), { position: "top-right" });
      setPending(prev => byId([...prev, ...response.points]));
    });
  };

  const importFile = file => {
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    upload({ method: "POST", url: "point_groups/points-ids-from-file/", post: form }, response => {
      setBusy(false);
      if (response?.errors?.length) Alert.error(`${t("file_error_uploaded")} ( ${response.errors[0]?.reason} )`, { position: "top-right" });
      if (!Array.isArray(response?.valid_ids)) return;
      setPending(prev => byId([...prev, ...response.valid_ids]));
      Alert.success(t("file_successfully_uploaded"), { position: "top-right" });
    });
    return false; // never let antd upload it itself
  };

  const finish = response => {
    setBusy(false);
    if (!response?.id)
      return Alert.error(t(mode === "new" ? "new_group_popup_error_save_text" : "new_group_popup_error_update_text"), { position: "top-right" });
    Alert.success(t("group_points_added"), { position: "top-right" });
    setOpen(false);
    if (onDone) onDone();
  };

  const submit = () => {
    const ids = pending.map(p => p.id);
    setBusy(true);
    if (mode === "new") {
      call({ method: "POST", url: "point_groups/", post: { name: name.trim(), available_for_supervisors: supervisors.map(s => s.id), points: ids } }, finish);
    } else {
      call({ method: "PATCH", url: `point_groups/${group.id}/`, post: { points: Array.from(new Set([...(members || []), ...ids])) } }, finish);
    }
  };

  /* Editing a group you did not create is the manager's call — the same rule the legacy panel
     applied to edit and delete. The server scopes the list to groups you own or are shared with;
     this is the narrower client-side half of it. */
  const mayEdit = !group || group.created_by?.id === currentUser?.id || currentUser?.role === "supervisor_manager";
  const dupes = members ? pending.filter(p => members.includes(p.id)).length : 0;
  const shortName = mode === "new" && name.trim().length > 0 && name.trim().length < MIN_NAME;
  const canSubmit = pending.length > 0 && !busy && (mode === "new" ? name.trim().length >= MIN_NAME : Boolean(group) && mayEdit);

  return (
    <>
      <Button size="small" icon={<GroupOutlined />} onClick={start}>
        {t("add_to_group_btn")}
      </Button>

      <Modal
        open={open}
        width={620}
        destroyOnHidden
        className="atg"
        title={t("add_to_group_btn")}
        onCancel={() => setOpen(false)}
        footer={
          <Flex align="center" justify="space-between" gap="small" wrap>
            <Button type="link" className="atg__manage" onClick={() => history.push("/point_groups")}>
              {t("group_manage_link")}
            </Button>
            <Flex gap="small">
              <Button onClick={() => setOpen(false)}>{t("cancel_btn")}</Button>
              <Button type="primary" loading={busy} disabled={!canSubmit} onClick={submit}>
                {t("add_to_group_btn")}
              </Button>
            </Flex>
          </Flex>
        }>
        <Form layout="vertical" component={false} colon={false}>
          <Segmented
            block
            className="atg__mode"
            value={mode}
            onChange={setMode}
            options={[
              { value: "existing", label: t("group_target_existing"), icon: <GroupOutlined /> },
              { value: "new", label: t("group_target_new"), icon: <PlusOutlined /> },
            ]}
          />

          {mode === "existing" ? (
            <>
              <Form.Item
                required
                label={t("group_field_label")}
                validateStatus={group && !mayEdit ? "error" : ""}
                help={group && !mayEdit ? t("new_group_popup_user_validate_error_text") : null}>
                <EntitySelect entity="point_groups" value={group} onChange={pickGroup} extraQuery={ACTIVE_ONLY} ariaLabel={t("group_field_label")} />
              </Form.Item>
              {group && members ? (
                <Flex className="atg__facts" gap="large" wrap>
                  <Typography.Text type="secondary">
                    {t("group_has_points")} <b>{members.length}</b>
                  </Typography.Text>
                  {dupes > 0 ? (
                    <Typography.Text type="secondary">
                      {t("group_already_in")} <b>{dupes}</b>
                    </Typography.Text>
                  ) : null}
                </Flex>
              ) : null}
            </>
          ) : (
            <>
              <Form.Item
                required
                htmlFor="atg-name"
                label={t("new_group_popup_field_name")}
                validateStatus={shortName ? "error" : ""}
                help={shortName ? t("new_group_popup_error_text_minimum") : null}>
                <Input id="atg-name" value={name} onChange={event => setName(event.target.value)} placeholder={t("new_group_popup_field_name_placeholder")} />
              </Form.Item>
              <Form.Item label={t("new_group_popup_field_group_available_users")}>
                <EntitySelect
                  entity="created_by"
                  isMulti
                  maxTagCount="responsive"
                  value={supervisors}
                  onChange={value => setSupervisors(value || [])}
                  placeholder={t("new_group_popup_field_users_placeholder")}
                  ariaLabel={t("new_group_popup_field_group_available_users")}
                />
              </Form.Item>
            </>
          )}

          <Flex className="atg__pointshead" align="center" gap="small" wrap>
            <Typography.Text strong>{t("group_points_to_add")}</Typography.Text>
            <span className="atg__n">{pending.length}</span>
            <span className="atg__spacer" />
            <Button size="small" type="text" loading={busy} onClick={addAllFiltered}>
              {t("new_group_popup_field_group_add_all_btn_text")} <span className="atg__n">{total}</span>
            </Button>
            <Upload accept=".csv,.xlsx" showUploadList={false} beforeUpload={importFile}>
              <Button size="small" type="text" icon={<UploadOutlined />}>
                {t("group_import_btn")}
              </Button>
            </Upload>
          </Flex>

          <div className="atg__points">
            {pending.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("group_no_points")} />
            ) : (
              <>
                {pending.slice(0, SHOWN).map(point => (
                  <Tag key={point.id} className="atg__chip" closable onClose={() => setPending(prev => prev.filter(p => p.id !== point.id))}>
                    {point.name}
                  </Tag>
                ))}
                {pending.length > SHOWN ? (
                  <Typography.Text type="secondary">{fill(t("group_more_hidden"), { n: pending.length - SHOWN })}</Typography.Text>
                ) : null}
              </>
            )}
          </div>
        </Form>
      </Modal>
    </>
  );
}
